import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Calendar } from 'lucide-react';
import * as ReactPopover from '@radix-ui/react-popover';
import { DayPicker } from 'react-day-picker';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-day-picker/dist/style.css';
import styled from 'styled-components';

import * as S from '../Input/styles';
import { Input } from '../Input';

const StyledPopoverContent = styled(ReactPopover.PopoverContent)`
  border-radius: 8px;
  background-color: white;
  padding: 4px;
  box-shadow: 1px 2px 5px 0px #00000033;
  margin-top: 24px;
`;

interface DynamicInputProps {
  name: string;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}

export const InputDate: React.FC<DynamicInputProps> = ({
  name,
  placeholder,
  disabled = false,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const realValue = watch(name);

  const parseDateString = (dateString: string): Date | undefined => {
    if (!dateString) return undefined;
    const parsed = parse(dateString, 'yyyy-MM-dd', new Date());
    return isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const [date, setDate] = useState<Date | undefined>(
    parseDateString(realValue),
  );
  const [month, setMonth] = useState<Date | undefined>(
    parseDateString(realValue) || new Date(),
  );
  const [isInputOpen, setIsInputOpen] = useState(false);

  const handleDateSelect = (selectedDate?: Date) => {
    if (selectedDate) {
      setDate(selectedDate);
      setValue(name, format(selectedDate, 'yyyy-MM-dd'));
      setMonth(selectedDate);
      setIsInputOpen(false);
    }
  };

  const handleClear = () => {
    setDate(undefined);
    setMonth(undefined);
    setValue(name, '');
  };

  return (
    <S.InputContainer>
      <ReactPopover.Popover
        onOpenChange={open => setIsInputOpen(open)}
        open={isInputOpen}
      >
        <Controller
          name={`${name}`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              disabled={disabled}
              type="date"
              pattern="[1-9][0-9]{3}"
              max={'9999-12-31'}
              onChange={e => {
                field.onChange(e);
                if (e.target.value.length === 10) {
                  const parsedDate = format(
                    parse(e.target.value, 'yyyy-MM-dd', new Date()),
                    'dd/MM/yyyy',
                  );
                  setDate(new Date(parsedDate));
                  setMonth(new Date(parsedDate));
                  setValue(name, e.target.value);
                }
              }}
              variant="date-input"
            />
          )}
        />

        <ReactPopover.Portal>
          <StyledPopoverContent>
            <DayPicker
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              month={month}
              onMonthChange={setMonth}
              locale={ptBR}
              modifiersStyles={{ selected: { background: '#F7941D' } }}
              footer={
                realValue ? (
                  <S.ClearButton onClick={handleClear}>Limpar</S.ClearButton>
                ) : null
              }
            />
          </StyledPopoverContent>
        </ReactPopover.Portal>

        <S.RightContent>
          <ReactPopover.PopoverTrigger
            asChild
            onClick={() => setIsInputOpen(prev => !prev)}
          >
            <Calendar color="#7c7e80" size={16} strokeWidth={2} />
          </ReactPopover.PopoverTrigger>
        </S.RightContent>
      </ReactPopover.Popover>
    </S.InputContainer>
  );
};
